import { useNotifications } from 'api/notifications/notifications.api'
import { t } from 'i18next'
import { ReactNode } from 'react'
import { useNavigate } from 'react-router'
import OptimizationFailedGuide from 'ui/molecules/OptimizationFailedGuide/OptimizationFailedGuide'
import Datetime from 'utils/datetime/datetime'
import {
  ACTIVE_STATUSES,
  getNotificationItem,
  getTime,
  isFailedOptimizationStatus,
  isTerminalOptimizationStatus,
  NotificationType,
  onLinkClicked,
} from '../NotificationCenter.helper'
import styles from '../NotificationCenter.module.less'

type NotificationOptimizationLogsProps = {
  selectedFilters: string[]
  date: ISODateTime
  treatRegularAsPublished: boolean
}

/**
 * The optimization log built from backend notifications. Superseded by
 * {@link NewNotificationOptimizationLogs}
 */
export function NotificationOptimizationLogs({
  selectedFilters,
  date,
  treatRegularAsPublished,
}: NotificationOptimizationLogsProps): ReactNode {
  const navigate = useNavigate()

  const { data: notifications } = useNotifications({
    startTime: Datetime.getStartOfDay(date),
    endTime: Datetime.getEndOfDay(date),
    type: 'optimization',
    exclude: '',
  })

  const matchesFilters = (item: NotificationType): boolean => {
    if (selectedFilters.length === 0) return true
    return selectedFilters.every((filter) => {
      switch (filter) {
        case 'failed':
          return isFailedOptimizationStatus(item.meta?.status)
        case 'finished':
          return item.meta?.status === 'finished'
        case 'manual':
          return Boolean(item.creator_name)
        case 'automatic':
          return !item.creator_name
        default:
          return false
      }
    })
  }

  const matchingItems = (notifications ?? []).filter(matchesFilters)
  const runningItems = matchingItems.filter((item) => item.meta?.status && ACTIVE_STATUSES.includes(item.meta.status))
  const completedItems = matchingItems.filter((item) => isTerminalOptimizationStatus(item.meta?.status))

  const renderItem = (item: NotificationType): ReactNode =>
    getNotificationItem(item, (route) => onLinkClicked(route, navigate), undefined, treatRegularAsPublished)

  return (
    <>
      <div className={styles.NotificationCenter_Events}>
        <span>{t('Running')}</span>
        {runningItems.length === 0 ? (
          <div className={styles.NotificationCenter_Events__noEvents}>{t('No running optimizations')}</div>
        ) : (
          runningItems.map((item) => <div key={item.id}>{renderItem(item)}</div>)
        )}
      </div>

      <div className={styles.NotificationCenter_Events}>
        <span>{t('Completed')}</span>
        <div className={styles.NotificationCenter_Events_Container}>
          {completedItems.length === 0 ? (
            <div className={styles.NotificationCenter_Events__noEvents}>{t('No completed optimizations')}</div>
          ) : (
            completedItems.map((item, index) => {
              const startTime = item.meta?.start_time
              const endTime = item.meta?.end_time
              return (
                <div key={item.id ?? index}>
                  {renderItem(item)}
                  {isFailedOptimizationStatus(item.meta?.status) && startTime && endTime && (
                    <OptimizationFailedGuide
                      startTime={Datetime.toISOString(startTime)}
                      endTime={Datetime.toISOString(endTime)}
                    />
                  )}
                  {getTime(item.updated_at)}
                </div>
              )
            })
          )}
        </div>
      </div>
    </>
  )
}
